import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Ref, useEffect, useImperativeHandle, useState } from 'react';
import { VariantProps } from 'tailwind-variants';

import { IconKitClose } from '@/icon/kit/Close';
import { UiKitButton, UiKitButtonProps } from '@/ui-kit/button';
import { UiKitDrawer, DialogContext } from '@/ui-kit/drawer';
import { useMediaQuery } from '@/ui-tool/media-query';

import { styles } from './UiKitDialog.styles';

type Variants = VariantProps<typeof styles>;

export interface UiKitDialogRef {
  close: () => void;
}

export interface UiKitDialogProps {
  children?: React.ReactNode;
  trigger?: React.ReactNode;
  /**
   * Заголовок диалога, требует Radix для screen reader
   */
  title: string;
  className?: string;
  dialogClassName?: string;
  drawerClassName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  vertical?: Variants['vertical'];
  horizontal?: Variants['horizontal'];
  background?: Variants['background'];
  /**
   * Ссылка на DOM-элемент контента диалога
   */
  ref?: Ref<HTMLDivElement>;
  fullScreen?: boolean;
  /**
   * При нажатии за пределами окна диалога, окно будет закрываться
   * @default true
   */
  dismissible?: boolean;
  /**
   * Окно не будет иметь backdrop и не блокирует интерфейс вне окна диалога
   * @default true
   */
  modal?: boolean;
  colorCross?: UiKitButtonProps['color'];
  /**
   * Ссылка на элемент управления диалогом
   */
  dialogRef?: Ref<UiKitDialogRef>;
  customCloseButton?: React.ReactNode;
  buttonClassName?: string;
  /**
   * Автоматически фокусируется на первом элементе ввода диалога
   * @default true
   */
  autoFocus?: boolean;
}

export function UiKitDialog({
  children,
  trigger,
  title,
  className,
  dialogClassName,
  drawerClassName,
  onOpenChange,
  vertical = 'center',
  horizontal = 'center',
  background = 'base',
  fullScreen = false,
  dismissible = true,
  modal = true,
  ref,
  colorCross = 'ghost',
  dialogRef,
  customCloseButton,
  autoFocus = true,
  buttonClassName,
  ...props
}: UiKitDialogProps) {
  const slots = styles({ vertical, horizontal, fullScreen, background });

  const [open, setOpen] = useState(props.open);

  const close = () => {
    setOpen(false);
  };

  useImperativeHandle(dialogRef, () => ({ close }));

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpen(props.open);
  }, [props.open]);

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    onOpenChange?.(open);
  };

  const isDesktop = useMediaQuery('sm');

  const [contentRef, setContentRef] = useState<HTMLDivElement | null>(null);

  const handleRef = (node: HTMLDivElement) => {
    setContentRef(node);

    if (ref) {
      if (typeof ref === 'function') {
        ref(node);
        return;
      }

      ref.current = node;
    }
  };

  const avoidDefaultDomBehavior = (event: Event) => {
    if (!dismissible) {
      event.preventDefault();
    }
  };

  const handleOpenAutoFocus = (event: Event) => {
    if (!autoFocus) {
      event.preventDefault();
    }
  };

  if (!isDesktop && !fullScreen) {
    return (
      <UiKitDrawer
        title={title}
        background={background}
        open={open}
        onOpenChange={handleOpenChange}
        trigger={trigger}
        className={drawerClassName}
      >
        {children}
      </UiKitDrawer>
    );
  }

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={handleOpenChange}
      modal={modal}
    >
      {trigger && <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>}
      <DialogContext.Provider value={{ contentRef }}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className={slots.overlay()} />
          <DialogPrimitive.Content
            autoFocus={autoFocus}
            onOpenAutoFocus={handleOpenAutoFocus}
            ref={handleRef}
            className={slots.contentContainer({ className: dialogClassName })}
            onPointerDownOutside={avoidDefaultDomBehavior}
            onInteractOutside={avoidDefaultDomBehavior}
          >
            {/* https://github.com/radix-ui/primitives/issues/2986 */}
            <DialogPrimitive.Title asChild>
              <span className="hidden">{title}</span>
            </DialogPrimitive.Title>
            <DialogPrimitive.Description asChild>
              <span className="hidden">{title}</span>
            </DialogPrimitive.Description>
            <div className={slots.content({ className })}>
              <DialogPrimitive.Close asChild>
                {customCloseButton ? (
                  customCloseButton
                ) : (
                  <UiKitButton
                    color={colorCross}
                    className={slots.closeButton({
                      className: buttonClassName
                    })}
                  >
                    <IconKitClose className={slots.closeButtonIcon()} />
                  </UiKitButton>
                )}
              </DialogPrimitive.Close>
              {children}
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogContext.Provider>
    </DialogPrimitive.Root>
  );
}
